import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, Printer, ShoppingBag, ArrowRight, ArrowLeft } from 'lucide-react';
import qrCodeImage from '../../assets/qrcode.png';

export default function OrderSuccess() {
    const location = useLocation();
    const orderDetails = location.state?.orderDetails || location.state?.order;

    if (!orderDetails) {
        return (
            <div className="bg-[#F2F2F2] min-h-screen py-16 flex items-center justify-center px-4">
                <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
                    <div className="w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
                        <ShoppingBag className="w-10 h-10 text-[#2C3E2B]" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-2">No Order Details Found</h2>
                    <p className="text-sm text-gray-500 mb-6">
                        We couldn't retrieve any details for this order. It might be because you refreshed the page or navigated here directly.
                    </p>
                    <Link
                        to="/products"
                        className="inline-flex items-center justify-center gap-2 w-full py-3.5 bg-[#2C3E2B] hover:bg-[#1e2d1e] text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-md transition-all hover:-translate-y-0.5 border-none cursor-pointer"
                    >
                        Go to Shop
                        <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        );
    }

    const sub = Number(orderDetails.finance?.subTotal || 0);
    const t = Number(orderDetails.finance?.tax || 0);
    const ship = Number(orderDetails.finance?.deliveryCharge || 0);
    const proc = Number(orderDetails.finance?.processingFee || 0);
    const disc = Number(orderDetails.finance?.discount || 0);
    const finalAmt = Number(orderDetails.finance?.finalAmount || 0);

    const cgstVal = parseFloat((t / 2).toFixed(2));
    const sgstVal = parseFloat((t - cgstVal).toFixed(2));
    
    const addressParts = [
        orderDetails.address?.streetAddress,
        orderDetails.address?.unitNumber ? `Unit ${orderDetails.address.unitNumber}` : null,
        [orderDetails.address?.city, orderDetails.address?.state, orderDetails.address?.zipCode].filter(Boolean).join(', ')
    ].filter(Boolean);

    const itemsList = orderDetails.items || [];
    const totalTaxableAmountCalc = itemsList.reduce((acc, item) => acc + (parseFloat(item.price || 0) * parseFloat(item.cartQty || item.quantity || 1)), 0);
    const totalGstAmountCalc = itemsList.reduce((acc, item) => {
        const unitPrice = parseFloat(item.price || 0);
        const qty = parseFloat(item.cartQty || item.quantity || 1);
        const taxable = unitPrice * qty;
        const taxPct = Number(item.taxPercent || item.product?.taxPercent || 0);
        return acc + (taxPct > 0 ? (taxable * taxPct / 100) : 0);
    }, 0);

    const totalTaxableAmount = totalTaxableAmountCalc > 0 ? totalTaxableAmountCalc : sub;
    const totalGstAmount = totalGstAmountCalc > 0 ? totalGstAmountCalc : t;
    
    const handlePrintInvoice = () => {
        window.print();
    };

    return (
        <main className="bg-[#F2F2F2] min-h-screen py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:p-0 flex flex-col items-center">
            <div className="max-w-4xl w-full">
                {/* Action bar — hidden on print */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4 print:hidden">
                    <Link
                        to="/products"
                        className="flex items-center gap-2 text-slate-600 hover:text-[#2C3E2B] transition-colors font-medium text-sm w-fit"
                    >
                        <ArrowLeft size={16} />
                        Continue Shopping
                    </Link>
                    <div className="flex gap-3">
                        <button
                            onClick={handlePrintInvoice}
                            className="flex items-center gap-2 bg-[#2C3E2B] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#1e2d1e] transition-all shadow-sm text-sm"
                        >
                            <Printer size={16} />
                            Print Invoice
                        </button>
                    </div>
                </div>

                {/* On-screen success message */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50/30 rounded-2xl p-6 text-center border border-green-100/50 mb-8 print:hidden shadow-sm">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-white shadow-sm mb-3">
                        <CheckCircle className="h-6 w-6 text-green-600 animate-bounce" />
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 mb-1 tracking-tight">Order Confirmed!</h1>
                    <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                        Thank you for your purchase. Your order has been placed successfully.
                    </p>
                </div>

                {/* Invoice card (from provided code) */}
                <div className="mb-1 text-[10px] text-gray-500 uppercase font-medium tracking-wide">ORIGINAL FOR RECIPIENT</div>
                <div className="bg-white rounded-sm shadow-sm border border-black text-[11px] text-black print:shadow-none print:border-black print:p-0 overflow-x-auto">
                    <div className="min-w-[700px]">
                        <div className="p-4 border-b border-black flex justify-between items-start">
                            <div className="flex items-center gap-4">
                                <div>
                                    <img src="/logo.png" alt="CHEFCO Logo" className="h-12 w-auto object-contain bg-[#2C3E2B] p-1.5 rounded" />
                                </div>
                                <div>
                                    <div className="text-[22px] font-bold text-[#2C3E2B] mb-1">CHEFCO HOSPITALITY PARTNERS LLP</div>
                                </div>
                            </div>
                            <div className="text-right text-[11px] leading-relaxed pt-1">
                                <strong>Invoice No:</strong> {orderDetails.orderId}<br />
                                <strong>Invoice Date:</strong> {orderDetails.purchaseDate}
                            </div>
                        </div>

                        <div className="flex border-b border-black">
                            <div className="w-1/2 p-3 border-r border-black">
                                <div className="font-bold text-[12px] mb-2 uppercase border-b-2 border-gray-200 inline-block pb-0.5">SHIPPED FROM</div>
                                <div className="leading-relaxed mt-1">
                                    <strong>CHEFCO HOSPITALITY PARTNERS LLP</strong><br />
                                    11/340, KT SONS Building. NOOLPUZHA,<br />
                                    Sulthan Bathery, Wayanad,<br />
                                    Kerala, 673592<br />
                                    <strong>Company GST No:</strong> 32AAWFC5009M1Z5<br />
                                    support@chefco.in
                                </div>
                            </div>
                            <div className="w-1/2 p-3">
                                <div className="font-bold text-[12px] mb-2 uppercase border-b-2 border-gray-200 inline-block pb-0.5">BILL TO</div>
                                <div className="leading-relaxed mt-1">
                                    <strong>{orderDetails.address?.fullName || '—'}</strong><br />
                                    {addressParts.length > 0 && <>{addressParts.map((part, i) => <span key={i}>{part}<br /></span>)}</>}
                                    Phone: {orderDetails.address?.mobileNumber || '—'}<br />
                                    PAN Number: —<br />
                                    GSTIN: {orderDetails.address?.gstin || orderDetails.address?.gstNo || orderDetails.gstin || '—'}<br />
                                    Place of Supply: Kerala
                                </div>
                            </div>
                        </div>

                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-center border-r border-b border-black w-[30px]">#</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold border-r border-b border-black">Item Name</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-center border-r border-b border-black w-[50px]">HSN</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-center border-r border-b border-black w-[50px]">Quantity</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-center border-r border-b border-black w-[50px]">Unit</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-right border-r border-b border-black w-[75px]">Price/Unit</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-right border-r border-b border-black w-[85px]">Taxable Amount</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-center border-r border-b border-black w-[70px]">GST</th>
                                    <th className="bg-[#2C3E2B] text-white p-2 font-semibold text-right border-b border-black w-[80px]">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itemsList.length > 0 ? (
                                    itemsList.map((product, index) => {
                                        const unitPrice = parseFloat(product.price || 0);
                                        const qty = parseFloat(product.cartQty || product.quantity || 1);
                                        const taxableAmt = unitPrice * qty;
                                        const code = (product.productCodeLetters || '') + (product.productCode || product.code || '');
                                        const hsn = product.hsn || product.hsnCode || product.product?.hsn || product.product?.hsnCode || '—';
                                        const taxPercent = Number(product.taxPercent || product.product?.taxPercent || 0);
                                        const taxAmt = taxPercent > 0 ? (taxableAmt * taxPercent / 100) : 0;
                                        const lineTotal = taxableAmt + taxAmt;
                                        const unit = product.unit || product.unitType || product.unitsOfMeasurement || product.pack || product.unitName || product.product?.unit || product.product?.unitType || product.product?.unitsOfMeasurement || product.product?.pack || '—';

                                        return (
                                            <tr key={index} className="print:break-inside-avoid" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                                <td className="p-2 text-center border-r border-black align-top">{index + 1}</td>
                                                <td className="p-2 border-r border-black align-top">
                                                    <div className="font-semibold">{product.productName || product.title || '—'}</div>
                                                    <div className="text-[9px] text-gray-500 mt-0.5">Code: {code || '—'}</div>
                                                </td>
                                                <td className="p-2 text-center border-r border-black align-top">{hsn}</td>
                                                <td className="p-2 text-center border-r border-black align-top">{qty}</td>
                                                <td className="p-2 text-center border-r border-black align-top">{unit}</td>
                                                <td className="p-2 text-right border-r border-black align-top">₹{unitPrice.toFixed(2)}</td>
                                                <td className="p-2 text-right border-r border-black align-top">₹{taxableAmt.toFixed(2)}</td>
                                                <td className="p-2 text-center border-r border-black align-top">
                                                    {taxPercent > 0 ? (
                                                        <div>
                                                            <div>₹{taxAmt.toFixed(2)}</div>
                                                            <div className="text-[9px] text-gray-500">({taxPercent}%)</div>
                                                        </div>
                                                    ) : '—'}
                                                </td>
                                                <td className="p-2 text-right align-top border-b-0 border-black font-semibold">₹{lineTotal.toFixed(2)}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="p-4 text-center">No products found.</td>
                                    </tr>
                                )}
                                
                                <tr className="font-bold bg-white border-t border-black print:break-inside-avoid" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                                    <td colSpan="6" className="p-2 text-right border-r border-black">Total</td>
                                    <td className="p-2 text-right border-r border-black">₹{totalTaxableAmount.toFixed(2)}</td>
                                    <td className="p-2 text-center border-r border-black">₹{totalGstAmount.toFixed(2)}</td>
                                    <td className="p-2 text-right font-bold">₹{(totalTaxableAmount + totalGstAmount).toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="print-summary-section print:break-inside-avoid" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                            <div className="flex justify-end p-4 py-3 border-t border-black">
                                <div className="w-64 space-y-1 text-sm">
                                    {disc > 0 && (
                                        <div className="flex justify-between text-red-600">
                                            <span>Discount</span>
                                            <span className="font-semibold">-₹{disc.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {ship > 0 && (
                                        <div className="flex justify-between">
                                            <span>Delivery</span>
                                            <span className="font-semibold text-gray-800">₹{ship.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {proc > 0 && (
                                        <div className="flex justify-between">
                                            <span>Processing Fee</span>
                                            <span className="font-semibold text-gray-800">₹{proc.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between">
                                        <span>CGST</span>
                                        <span className="font-semibold text-gray-800">₹{cgstVal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>SGST</span>
                                        <span className="font-semibold text-gray-800">₹{sgstVal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-bold border-t border-gray-200 pt-2 mt-2">
                                        <span>Total GST</span>
                                        <span className="font-semibold text-gray-800">₹{t.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between font-bold pt-2 text-base">
                                        <span>Grand Total</span>
                                        <span>₹{finalAmt.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex border-t border-black">
                                <div className="w-1/2 p-3 border-r border-black flex flex-col xl:flex-row items-center justify-between gap-4">
                                    <div className="w-full xl:w-2/3">
                                        <div className="font-bold text-[12px] mb-3">Bank Details</div>
                                        <table className="w-full">
                                            <tbody>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">Account holder:</td>
                                                    <td className="py-0.5 text-right">chefco hospitality partners llp</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">Account number:</td>
                                                    <td className="py-0.5 text-right">10690200020234</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">Bank:</td>
                                                    <td className="py-0.5 text-right">Federal Bank</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">Branch:</td>
                                                    <td className="py-0.5 text-right">sulthan bathery</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">IFSC code:</td>
                                                    <td className="py-0.5 text-right">FDRL0001069</td>
                                                </tr>
                                                <tr>
                                                    <td className="py-0.5 font-semibold">UPI ID:</td>
                                                    <td className="py-0.5 text-right">chefcobathery1qr@fbl</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <img src={qrCodeImage} alt="UPI QR Code" className="w-24 h-24 object-contain rounded" />
                                    </div>
                                </div>
                                <div className="w-1/2 p-3 flex flex-col justify-end items-center pb-6">
                                    <div className="border-t border-black w-4/5 text-center pt-2 font-semibold text-sm">
                                        Authorized Signatory
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="text-center text-[10px] text-gray-500 mt-4 leading-snug print:hidden">
                    <div className="font-medium">Thank You For Your Business !</div>
                    <div className="text-[8px] mt-0.5">This is a computer generated bill.</div>
                </div>
            </div>
        </main>
    );
}

